import type { RecordsDiff } from "@tldraw/store";
import type { TLRecord, TLStore } from "@tldraw/tlschema";
import { isEqual } from "@tldraw/utils";
import { mergeFields } from "../../../shared/record-fields";

/** Remote edits supersede the same fields in both directions of local history.
 * Rebasing the open step also prevents a later local edit from absorbing remote
 * values when its diff is squashed into the current gesture. */
export function rebaseHistoryDiff(
  diff: RecordsDiff<TLRecord>,
  remote: RecordsDiff<TLRecord>,
  derived = false,
): RecordsDiff<TLRecord> {
  const next: RecordsDiff<TLRecord> = {
    added: { ...diff.added },
    updated: { ...diff.updated },
    removed: { ...diff.removed },
  };
  for (const id of [
    ...Object.keys(remote.added),
    ...Object.keys(remote.updated),
    ...Object.keys(remote.removed),
  ] as TLRecord["id"][]) {
    // A remotely edited creation now contains someone else's work. A remote
    // deletion/recreation must never be undone by an older local step.
    if (derived && next.added[id] && remote.updated[id]) {
      const [before, after] = remote.updated[id];
      next.added[id] = mergeFields(next.added[id], before, after, false);
    } else delete next.added[id];
    delete next.removed[id];
    const local = next.updated[id],
      change = remote.updated[id];
    if (!local) continue;
    if (!change) {
      delete next.updated[id];
      continue;
    }
    const before = mergeFields(local[0], change[0], change[1], false);
    const after = mergeFields(local[1], change[0], change[1], false);
    if (isEqual(before, after)) delete next.updated[id];
    else next.updated[id] = [before, after];
  }
  return next;
}

/** Reapply only fields still owned by this step; never resurrect newer removals. */
export function applicableHistoryDiff(
  store: TLStore,
  diff: RecordsDiff<TLRecord>,
  canChange: (
    before: TLRecord | undefined,
    after: TLRecord | undefined,
  ) => boolean = () => true,
): RecordsDiff<TLRecord> {
  const next: RecordsDiff<TLRecord> = { added: {}, updated: {}, removed: {} };
  for (const record of Object.values(diff.added))
    if (!store.get(record.id) && canChange(undefined, record))
      next.added[record.id] = record;
  for (const [before, after] of Object.values(diff.updated)) {
    const current = store.get(after.id);
    if (!current || !canChange(current, after)) continue;
    const merged = mergeFields(current, before, after, true);
    if (!isEqual(current, merged)) next.updated[after.id] = [current, merged];
  }
  for (const record of Object.values(diff.removed)) {
    const current = store.get(record.id);
    if (!isEqual(current, record) || !canChange(current, undefined)) continue;
    // The sync authorizers deliberately retain discussions. Their inverse is a
    // tombstone, so undo never sends a forbidden hard comment/thread deletion.
    if (record.typeName === "comment" || record.typeName === "comment-thread") {
      if (!record.isDeleted)
        next.updated[record.id] = [record, { ...record, isDeleted: true }];
    } else next.removed[record.id] = record;
  }

  // A discussion with somebody else's surviving reply stays visible. The
  // current user's initial comment may still be removed using its tombstone.
  for (const record of store.allRecords()) {
    if (record.typeName !== "comment" && record.typeName !== "comment-reaction")
      continue;
    const changed = next.updated[record.id]?.[1] ?? record;
    if (
      next.removed[record.id] ||
      ("isDeleted" in changed && changed.isDeleted)
    )
      continue;
    const thread = next.updated[record.threadId];
    if (thread?.[1].typeName === "comment-thread" && thread[1].isDeleted)
      delete next.updated[record.threadId];
  }

  // A locally created container may have acquired remote children, bindings or
  // discussions. Keep it (and its ancestors) rather than deleting that work via
  // the editor's cascading shape hooks.
  let protectedParent: boolean;
  do {
    protectedParent = false;
    for (const record of store.allRecords()) {
      if (next.removed[record.id]) continue;
      const dependencies =
        record.typeName === "shape"
          ? [
              record.parentId,
              ...("assetId" in record.props && record.props.assetId
                ? [record.props.assetId]
                : []),
            ]
          : record.typeName === "binding"
            ? [record.fromId, record.toId]
            : record.typeName === "comment-thread" &&
                record.anchor.type === "shape"
              ? [record.anchor.shapeId]
              : record.typeName === "comment"
                ? [record.threadId]
                : record.typeName === "comment-reaction"
                  ? [record.threadId, record.commentId]
                  : [];
      for (const id of dependencies) {
        if (!next.removed[id]) continue;
        delete next.removed[id];
        protectedParent = true;
      }
    }
  } while (protectedParent);
  return next;
}
